import React, { useState } from 'react'

import { Dropdown, type DropdownOption } from '@/components/ui'
import { useApi } from '@/hooks'
import { assetDropdownLabel } from '@/insights/common/assetLabel'
import { AssetTypeFilter, type Asset } from '@/types/dashboard-types'
import { type DataSource } from '@/types/data-source'

import { type AssetDateRange, useAssetCurrentStatus } from './useAssetCurrentStatus'

interface Params {
  companyId?: number
  assetIds?: string[]
  includeSelectAll?: boolean
  assetType?: AssetTypeFilter
  onChange: (assetIds?: string[]) => void
  /** Which back end to list assets from; defaults to Fresh. */
  dataSource?: DataSource
  /** The insight's date selection, which the support-only "has current data" filter uses. */
  dateRange?: AssetDateRange
}

export const AssetDropdownMulti: React.FC<Params> = ({
  assetIds,
  companyId,
  includeSelectAll,
  assetType,
  onChange,
  dataSource,
  dateRange,
}) => {
  const { api } = useApi()
  const status = useAssetCurrentStatus(companyId, dateRange, dataSource)
  const [options, setOptions] = React.useState<DropdownOption[]>([])
  const [placeholder, setPlaceholder] = React.useState('')
  const [lastCompanyId, setLastCompanyId] = useState(companyId)
  const [selected, setSelected] = useState(assetIds ?? [])

  React.useEffect(() => {
    setSelected(assetIds ?? [])
  }, [assetIds])

  React.useEffect(() => {
    if (!api) {
      return
    }
    if (!companyId) {
      setOptions([])
      setPlaceholder('Choose a company to load assets...')
      return
    }
    const fn = async () => {
      setPlaceholder('Loading...')
      if (lastCompanyId !== companyId) {
        setOptions([])
        setSelected([])
      }
      setLastCompanyId(companyId)
      const response = await api.get<Asset[]>({
        path: `/fresh/assets?companyId=${companyId}&skip=0&take=10000&deleted=false${
          assetType && assetType !== AssetTypeFilter.All ? `&assetType=${assetType}` : ''
        }${dataSource ? `&dataSource=${dataSource}` : ''}`,
        origin: 'AssetDropdown',
        expiresIn: '2h',
      })
      if (response.error || !response.data) {
        console.error(response.error)
        setPlaceholder('Error loading assets. Contact support.')
        setOptions([])
      } else {
        setPlaceholder('Choose one or more assets...')
        setOptions(
          response.data.map((a) => {
            return {
              label: assetDropdownLabel(a, dataSource),
              value: String(a.id),
            }
          })
        )
      }
    }
    fn()
  }, [api, companyId, assetType])

  // Memoized: a company can have thousands of assets, and rebuilding them all (icons included)
  // on every render makes typing in the picker lag.
  const { isShown, iconFor } = status
  const shownOptions = React.useMemo(
    () =>
      options
        .filter((o) => isShown(o.value, selected.includes(o.value)))
        .map((o) => ({ ...o, icon: iconFor(o.value) })),
    [options, isShown, iconFor, selected]
  )

  return (
    <div>
      <label>Assets</label>
      <Dropdown
        options={shownOptions}
        handleMultiSelectItem={(opt: DropdownOption[]) => {
          const selectedValues = opt.filter((option) => option.value !== '').map((option) => option.value)
          const assetIds = selectedValues.length > 0 ? selectedValues : undefined
          onChange(assetIds)
        }}
        selectedItem={shownOptions.filter((o) => Array.isArray(selected) && selected.includes(o.value))}
        placeholder={placeholder}
        animateDots={/^Loading/.test(placeholder)}
        isMultiSelect
        includeSelectAll={includeSelectAll}
      />
      {status.toggle(options.map((o) => o.value))}
    </div>
  )
}
